import React, { useEffect, useState } from "react";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import LoadingAnimation from "./components/LoadingAnimation";

// Helper functions to generate date options for filters
const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = ['הכל'];
    for (let i = currentYear; i >= currentYear - 5; i--) {
        years.push(i.toString());
    }
    return years;
};
const getMonths = () => ['הכל', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const getDays = () => {
    const days = ['הכל'];
    for (let i = 1; i <= 31; i++) {
        days.push(i.toString().padStart(2, '0'));
    }
    return days;
};

export default function History() {
    const [allHistory, setAllHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterDate, setFilterDate] = useState({
        year: new Date().getFullYear().toString(),
        month: (new Date().getMonth() + 1).toString().padStart(2, '0'),
        day: new Date().getDate().toString().padStart(2, '0'),
    });
    const [userId, setUserId] = useState(null);
    const navigate = useNavigate();

    // Get current user ID
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUserId(user ? user.uid : null);
        });
        return () => unsubscribe();
    }, []);

    // Fetch all history orders once on component mount and when userId changes
    useEffect(() => {
        if (!userId) return;
        const fetchHistory = async () => {
            setIsLoading(true);
            const q = query(collection(db, "users", userId, "orderHistory"), orderBy("completedAt", "desc"));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllHistory(data);
            setIsLoading(false);
        };
        fetchHistory();
    }, [userId]);

    // Apply filter whenever the date or the source data changes
    useEffect(() => {
        const applyFilter = () => {
            if (!allHistory.length) {
                setFilteredHistory([]);
                return;
            }

            const { year, month, day } = filterDate;
            const filtered = allHistory.filter(order => {
                if (!order.completedAt || !order.completedAt.toDate) return false;
                const date = order.completedAt.toDate();
                const orderYear = date.getFullYear().toString();
                const orderMonth = (date.getMonth() + 1).toString().padStart(2, '0');
                const orderDay = date.getDate().toString().padStart(2, '0');

                const yearMatch = year === 'הכל' || year === orderYear;
                const monthMatch = month === 'הכל' || month === orderMonth;
                const dayMatch = day === 'הכל' || day === orderDay;

                return yearMatch && monthMatch && dayMatch;
            });
            setFilteredHistory(filtered);
        };
        applyFilter();
    }, [filterDate, allHistory]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterDate(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="container mx-auto px-4 py-8">
            {isLoading && <LoadingAnimation message="טוען היסטוריה..." />}
            <div className="max-w-4xl mx-auto">
                <header className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-amber-400">היסטוריית הזמנות</h1>
                    <button
                        onClick={() => navigate("/admin")}
                        className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        חזור להזמנות פעילות
                    </button>
                </header>

                {/* --- Filters --- */}
                <div className="bg-gray-800 p-4 rounded-lg mb-6 flex flex-col sm:flex-row justify-center items-center gap-4">
                     <span className="font-semibold">סנן לפי תאריך:</span>
                     <select name="day" value={filterDate.day} onChange={handleFilterChange} className="bg-gray-700 border border-gray-600 rounded p-2 text-right">
                        {getDays().map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select name="month" value={filterDate.month} onChange={handleFilterChange} className="bg-gray-700 border border-gray-600 rounded p-2 text-right">
                        {getMonths().map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select name="year" value={filterDate.year} onChange={handleFilterChange} className="bg-gray-700 border border-gray-600 rounded p-2 text-right">
                        {getYears().map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>

                {/* --- History List --- */}
                {filteredHistory.length > 0 ? (
                    <div className="space-y-3">
                        {filteredHistory.map(order => (
                            <div key={order.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 text-right">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="font-bold text-lg text-white">{order.name} - (תור: {order.queueNumber || 'N/A'})</span>
                                    <span className="text-sm text-gray-400">{order.completedAt?.toDate().toLocaleString('he-IL')}</span>
                                </div>
                                <ul className="list-disc list-inside text-gray-300 pr-4">
                                     {order.items?.map((item, i) => (
                                         <li key={i}>{item.quantity}x {item.parentType === 'chamin' ? `צ'ולנט ${item.size}` : item.label}</li>
                                     ))}
                                </ul>
                                <div className="text-left font-bold text-amber-400 mt-2">סה"כ: {order.totalPrice} ₪</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-gray-400 mt-10">
                        <p className="text-xl">לא נמצאו הזמנות בתאריך שנבחר.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
